import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { renewCollaborateurManually, getCollaborateurById } from '../../../services/CollaborateurService';
import Transition from './common/Transition';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';



const ProlongFormDialog = ({ open, onClose, employeeId, employeeContratId, snackbarShowMessage, setEmployee, currentDateFinContrat, onBack, setReloadNotifications,
    reloadNotifications }) => {
    const [newDateFin, setNewDateFin] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && currentDateFinContrat) {
            const currentEnd = new Date(currentDateFinContrat);
            const nextDay = new Date(currentEnd);
            nextDay.setDate(currentEnd.getDate() + 1);
            setNewDateFin(nextDay);
        } else if (open && !currentDateFinContrat) {
            const today = new Date();
            today.setDate(today.getDate() + 1);
            setNewDateFin(today);
        }
    }, [open, currentDateFinContrat]);

    const handleProlongContract = async () => {
        if (!newDateFin) {
            snackbarShowMessage('Veuillez sélectionner une nouvelle date de fin de contrat.', 'warning');
            return;
        }

        setSaving(true);
        try {
            const dateDebut = new Date();
            const dateFin = newDateFin;
            const formattedDateDebut = dateDebut.toISOString().split('T')[0];
            const formattedDateFin = dateFin.toISOString().split('T')[0];


            const months =
                (dateFin.getFullYear() - dateDebut.getFullYear()) * 12 +
                (dateFin.getMonth() - dateDebut.getMonth());
            const periodeContrat = `${months || 1}mois`;

            await renewCollaborateurManually(employeeId, {
                dateDebut: formattedDateDebut,
                dateFinContrat: formattedDateFin,
                contratId: employeeContratId,
                periodeContrat
            });

            const { data } = await getCollaborateurById(employeeId);
            const updatedEmployeeData = {
                ...data,
                contratId: (data.contrat && typeof data.contrat === 'object' ? data.contrat.id : data.contrat) || '',
                renouvellements: data.renouvellements ?? false
            };

            setEmployee(updatedEmployeeData);
            setReloadNotifications(!reloadNotifications);
            snackbarShowMessage(<span><CheckCircleIcon style={{ verticalAlign: 'middle', marginRight: 6 }} />Contrat prolongé avec succès </span>, 'success');
            onClose();
        } catch (error) {
            console.error("Erreur lors de la prolongation du contrat:", error);
            snackbarShowMessage(<span style={{ display: 'flex', alignItems: 'center' }}>
                <ErrorOutlineIcon style={{ color: '#d32f2f', marginRight: 6 }} />Erreur lors de la prolongation du contrat: ${error.message} </span>, 'error');
        } finally {
            setSaving(false);
        }
    };


    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            TransitionComponent={Transition}
            keepMounted
            slotProps={{ paper: {
                sx: {
                    borderRadius: 6,
                    background: 'rgba(255, 255, 255, 0.75)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    overflow: 'hidden',
                }
            } }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    fontFamily: `'Orbitron', 'Segoe UI', sans-serif`,
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    color: '#0f172a',
                    pt: 4,
                }}
            >
                Prolonger le Contrat
            </DialogTitle>

            <DialogContent sx={{ px: 4, pt: 2, pb: 3 }}>
                <Typography
                    variant="body1"
                    align="center"
                    sx={{
                        fontFamily: `'Segoe UI', Roboto, sans-serif`,
                        color: '#334155',
                        fontSize: '1rem',
                        letterSpacing: '0.3px',
                        mb: 2
                    }}
                >
                    Sélectionnez la nouvelle date de fin de contrat.
                </Typography>
                <Box sx={{ width: '100%' }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            sx={{ width: '100%' }}
                            value={newDateFin}
                            onChange={(newValue) => setNewDateFin(newValue)}
                            disabled={saving}
                            slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                        />
                    </LocalizationProvider>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    justifyContent: 'center',
                    gap: 2,
                    px: 3,
                    pb: 4,
                    flexWrap: 'wrap'
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onBack}
                    disabled={saving}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '20px',
                        px: 3,
                        py: 1,
                        color: '#64748b',
                        borderColor: '#64748b',
                        '&:hover': {
                            background: '#e2e8f0',
                            borderColor: '#475569',
                        },
                    }}
                >
                    Retour
                </Button>
                <Button
                    variant="contained"
                    onClick={handleProlongContract}
                    disabled={saving || !newDateFin}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '20px',
                        px: 3,
                        py: 1,
                        background: 'linear-gradient(90deg, #3b82f6, #03407b)',
                        color: '#fff',
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(90deg, #2563eb, #02357a)',
                        },
                    }}
                >
                    {saving ? 'Prolongation...' : 'Prolonger'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProlongFormDialog;